<template>
  <div>
    <v-card class="elevation-0">
      <v-card-title>Base properties</v-card-title>
      <v-card-text>
        <v-row>
          <v-col>
            <v-text-field
              v-model="item.name"
              label="Name"
              :error-messages="formErrors.name"
              @change="formErrors.name = null"
            />
          </v-col>
          <v-col>
            <v-text-field
              v-model="item.prefix"
              label="Prefix"
              hint="This is added to the product name where this stack unit is enabled."
              :error-messages="formErrors.prefix"
              :persistent-hint="helpToggle"
              @change="formErrors.prefix = null"
            />
          </v-col>
        </v-row>
        <v-textarea
          v-model="item.description"
          label="Description"
          maxlength="120"
          counter
        />
      </v-card-text>
    </v-card>

    <v-row>
      <v-col>
        <v-card class="elevation-0">
          <v-card-title>Number of items</v-card-title>
          <v-card-subtitle
            >The number of single items, or stock keeping units in this stack
            unit.</v-card-subtitle
          >
          <v-card-text>
            <v-list>
              <v-list-item>
                <v-list-item-action>
                  <v-switch
                    v-model="item.exposed_fields.sku_count"
                    :title="expose_parameter"
                  />
                </v-list-item-action>

                <v-list-item-content>
                  <v-row>
                    <v-col>
                      <v-text-field
                        v-model="item.count"
                        label="Count"
                        type="number"
                        :min="1"
                        :error-messages="formErrors.count"
                        @change="formErrors.count = null"
                      />
                    </v-col>
                    <v-col>
                      <v-select
                        v-model="item.count_type"
                        :items="item_types"
                        label="Item type"
                        item-text="label"
                        item-value="value"
                        :error-messages="formErrors.count_type"
                      />
                    </v-col>
                  </v-row>
                </v-list-item-content>
                <v-list-item-action />
              </v-list-item>
            </v-list>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col>
        <v-card class="elevation-0">
          <v-card-title>Measurement options</v-card-title>
          <v-card-subtitle
            >Leave empty to automatically calculate a value based on the stock
            keeping unit, if possible.</v-card-subtitle
          >

          <v-card-text>
            <v-list>
              <v-list-item>
                <v-list-item-action>
                  <v-switch
                    v-model="item.exposed_fields.shape"
                    :title="expose_parameter"
                  />
                </v-list-item-action>

                <v-list-item-title>
                  <v-select
                    v-model="item.shape"
                    :items="shapes"
                    label="Shape"
                    item-text="label"
                    item-value="value"
                    :error-messages="formErrors.shape"
                    hint="Hint!"
                    :persistent-hint="helpToggle"
                    @change="formErrors.shape = null"
                  />
                </v-list-item-title>
              </v-list-item>

              <v-list-item
                v-for="measurement in measurements"
                :key="measurement.model"
              >
                <v-list-item-action>
                  <v-switch
                    v-model="item.exposed_fields[measurement.model]"
                    :title="expose_parameter"
                    @change="formErrors[measurement.model] = null"
                  />
                </v-list-item-action>

                <v-list-item-title>
                  <v-text-field
                    v-model="item[measurement.model]"
                    suffix="mm"
                    type="number"
                    :label="measurement.label"
                    :min="0"
                    :error-messages="formErrors[measurement.model]"
                    :hint="measurement.hint"
                    :persistent-hint="helpToggle"
                    @change="formErrors[measurement.model] = null"
                  />
                </v-list-item-title>
              </v-list-item>

              <v-list-item>
                <v-list-item-action>
                  <v-switch
                    v-model="item.exposed_fields.weight"
                    :title="expose_parameter"
                  />
                </v-list-item-action>

                <v-list-item-content>
                  The weight is calculated from the stock keeping unit and the
                  number of items in the stack unit.
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col>
        <v-card class="elevation-0">
          <v-card-title>Stacking options</v-card-title>
          <v-card-subtitle
            >Another help text about enabling form elements and settings default
            values.</v-card-subtitle
          >
          <v-card-text>
            <v-list>
              <v-list-item
                v-for="orientation in orientations"
                :key="orientation.model"
              >
                <v-list-item-action>
                  <v-switch
                    v-model="item.exposed_fields[orientation.model]"
                    title="Allow this parameter to be overridden."
                  />
                </v-list-item-action>
                <v-list-item-title>
                  <v-checkbox
                    v-model="item.orientation[orientation.model]"
                    :label="orientation.label"
                    :hint="orientation.hint"
                    :persistent-hint="helpToggle"
                  />
                </v-list-item-title>
              </v-list-item>

              <v-list-item>
                <v-list-item-action>
                  <v-switch
                    v-model="item.exposed_fields.stack_index"
                    :title="expose_parameter"
                  />
                </v-list-item-action>
                <v-text-field
                  v-model="item.stack_index"
                  label="Stack index"
                  type="number"
                  hint="Lower stack indexes can only be stacked on higher indexes."
                  :persistent-hint="helpToggle"
                  :min="0"
                />
              </v-list-item>
            </v-list>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
export default {
  props: ['item', 'formErrors', 'helpToggle'],

  data() {
    return {
      expose_parameter: 'Allow this parameter to be overridden.',
      shapes: [
        { label: 'Box', value: 'box' },
        { label: 'Cylinder', value: 'cylinder' },
      ],
      orientations: [
        { model: 'flat_ok', label: 'As defined', hint: 'Hint!' },
        { model: 'turnable', label: 'Can be rotated 90°', hint: 'Hint!' },
        { model: 'side_up', label: 'Side up', hint: 'Hint!' },
        { model: 'end_up', label: 'End up', hint: 'Hint!' },
        { model: 'top_only', label: 'Top only', hint: 'Hint!' },
        { model: 'bottom_only', label: 'Bottom only', hint: 'Hint!' },
      ],
      measurements: [
        { model: 'diameter', label: 'Diameter', hint: 'Hint!' },
        { model: 'length', label: 'Length', hint: 'Hint!' },
        { model: 'width', label: 'Width', hint: 'Hint!' },
        { model: 'height', label: 'Height', hint: 'Hint!' },
      ],
      item_types: [
        { label: 'SKU', value: 'sku' },
        { label: 'Items', value: 'item' },
      ],
    }
  },

  methods: {
    back() {
      this.$router.back()
    },
  },
}
</script>
